import { FormControl, Grid, Radio, RadioGroup } from '@mui/material';
import { memo, useCallback, useMemo } from 'react';
import { PmiTextField } from 'shell/components/shared/text-field';
import { FormikErrors, FormikTouched } from 'formik';
import { SectionVM } from 'civil-status/view-models/step-form';
import { FormControlLabel } from '@mui/material';
import Foreign from '../../shared/foreign';
import { ForeignForm } from 'civil-status/view-models/accordion-generality-form';
import Italian from '../../shared/italian';
import { ItalianForm } from 'shell/view-models/italian-form';
import { DatePicker } from '@mui/lab';
import { ConfigurationType } from 'civil-status/constants/configuration-type';

interface BaseCivilUnionDataProps {
  disabled?: boolean;
  values?: SectionVM;
  errors?: FormikErrors<SectionVM>;
  touched?: FormikTouched<SectionVM>;
  onChange: (key: keyof SectionVM, value: SectionVM[keyof SectionVM]) => void;
  onChanges: (values: SectionVM) => void;
  configuration?: string;
}

const BaseCivilUnionDataComponent = ({
  disabled = false,
  values,
  errors,
  touched,
  onChange,
  onChanges,
  configuration,
}: BaseCivilUnionDataProps): React.ReactElement => {
  const isBaseUcst: boolean = useMemo(() => {
    return configuration === ConfigurationType.BASEUCST;
  }, [configuration]);

  const onFieldForeignChange = useCallback(
    (key, value) => {
      onChanges({
        ...(values || {}),
        foreign: {
          ...(values || {}).foreign,
          [key]: value,
        },
      });
    },
    [values, onChanges]
  );

  const onFieldItalianChange = useCallback(
    (key, value) => {
      onChange('italian', {
        ...(values || {}).italian,
        [key]: value,
      });
    },
    [values, onChange]
  );

  const onFieldsItalianChange = useCallback(
    (valuesNew) => {
      onChange('italian', {
        ...(values || {}).italian,
        ...valuesNew,
      });
    },
    [values, onChange]
  );

  const onFieldsForeignChange = useCallback(
    (valuesNew) => {
      onChanges({
        ...(values || {}),
        foreign: {
          ...(valuesNew || {}),
        },
      });
    },
    [values, onChange]
  );

  return (
    <Grid container spacing={2} item>
      {isBaseUcst ? (
        <Grid item xs={12} md={4}>
          <DatePicker
            label="Data unione civile *"
            aria-label="Data unione civile"
            value={values?.dataCivilUnion || null}
            onChange={(date) => {
              onChange('dataCivilUnion', date);
            }}
            disabled={disabled}
            renderInput={(params) => (
              <PmiTextField
                {...params}
                id="dataCivilUnion"
                fullWidth
                helperText={errors?.dataCivilUnion}
                error={touched?.dataCivilUnion && Boolean(errors?.dataCivilUnion)}
              />
            )}
          />
        </Grid>
      ) : undefined}
      <Grid item xs={12}>
        <FormControl component="fieldset">
          <RadioGroup
            row
            aria-label="Nascita"
            id="isForeignBorn"
            value={values?.isForeign || 'false'}
            onChange={() => {
              onChanges({
                isForeign: !values?.isForeign,
                italian: {
                  location: '',
                  province: '',
                  municipality: null,
                },
                foreign: {
                  state: null,
                  province: '',
                  municipality: null,
                },
              });
            }}
          >
            <FormControlLabel
              value="false"
              control={<Radio />}
              label="Italia"
              disabled={disabled}
            />
            <FormControlLabel value="true" control={<Radio />} label="Estero" disabled={disabled} />
          </RadioGroup>
        </FormControl>
      </Grid>
      {values?.isForeign ? (
        <Foreign
          disabledComponent={disabled}
          values={values?.foreign}
          onChange={onFieldForeignChange}
          onChanges={onFieldsForeignChange}
          errors={errors?.foreign as FormikErrors<ForeignForm>}
          touched={touched?.foreign as unknown as FormikTouched<ForeignForm>}
          labelState="Stato ente"
          required
          isWithLocationForeign
        />
      ) : (
        <Italian
          disabledComponent={disabled}
          values={values?.italian}
          onChange={onFieldItalianChange}
          onChanges={onFieldsItalianChange}
          errors={errors?.italian as FormikErrors<ItalianForm>}
          touched={touched?.italian as unknown as FormikTouched<ItalianForm>}
          fromDate={values?.dataCivilUnion}
          labelMunicipality="Comune"
          labelProvince="Provincia"
          required
        />
      )}
    </Grid>
  );
};

BaseCivilUnionDataComponent.displayName = 'Form Base Civil Union component';

const BaseCivilUnionData = memo(BaseCivilUnionDataComponent);

export default BaseCivilUnionData;
